import React, { useEffect, useState, useRef } from "react";
import Transcript from "./components/Transcript";
import { platformClient } from "./utils/genesysCloudUtils";
import { clientConfig } from "./config/clientConfig";
import "./App.css";

const App: React.FC = () => {
  const { debug, clientId, redirectUri, region, conversationId } = clientConfig;

  const [messages, setMessages] = useState<
    { text: string; speaker: string; dateTime: string }[]
  >([]);
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  const client = platformClient.ApiClient.instance;
  const notificationsApi = new platformClient.NotificationsApi();
  const usersApi = new platformClient.UsersApi();

  useEffect(() => {
    const start = async () => {
      try {
        client.setEnvironment(region || "");
        client.setPersistSettings(true, "_mm_");

        if (debug) console.log("Logging in to Genesys Cloud");
        await client.loginImplicitGrant(clientId || "", redirectUri || "", {});

        const user = await usersApi.getUsersMe({});
        console.log(user);

        const channelid = sessionStorage.getItem("_channelid");
        if (channelid) {
          if (debug) console.log("channelid already exists...", channelid);
          const transcriptTopic = `v2.conversations.${conversationId}.transcription`;
          await notificationsApi.postNotificationsChannelSubscriptions(channelid, [
            { id: transcriptTopic },
          ]);
          if (debug) console.log(`Subscribed to topic ${transcriptTopic}`);
        } else {
          const channel = await notificationsApi.postNotificationsChannels();
          if (debug) console.log("Created Notification Channel: ", channel);

          const transcriptTopic = `v2.conversations.${conversationId}.transcription`;
          if (channel.id) {
            await notificationsApi.postNotificationsChannelSubscriptions(channel.id, [
              { id: transcriptTopic },
            ]);
          } else {
            throw new Error("Channel ID is undefined");
          }
          if (debug) console.log(`Subscribed to topic ${transcriptTopic}`);
          sessionStorage.setItem("_channelid", channel.id);
        }

        const socket = new WebSocket(
          `wss://streaming.${region}/channels/${sessionStorage.getItem(
            "_channelid"
          )}`
        );

        socket.onmessage = async (event) => {
          const details = JSON.parse(event.data);
          if (details.topicName.includes("transcription")) {
            if (debug) console.log("Transcript Notification: ", details);
            if (!details.eventBody.transcripts) return;
            for (const alternatives of details.eventBody.transcripts) {
              for (const words of alternatives.alternatives) {
                const user = alternatives.channel;
                let text = "";
                for (const word of words.words) {
                  if (word.confidence > 0.5) {
                    text += ` ${word.word}`;
                  }
                }
                if (user === "INTERNAL") {
                  createAgentMsg(text, details.eventBody.eventTime);
                } else {
                  createCustomerMsg(text, details.eventBody.eventTime);
                }
              }
            }
          }
        };
        if (debug)
          console.log(
            `Waiting for events on wss://streaming.${region}/channels/${sessionStorage.getItem(
              "_channelid"
            )}`
          );
      } catch (err) {
        console.error("Error: ", err);
      }
    };

    start();
  }, [debug]);

  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  const createCustomerMsg = (message: string, dateTime: string) => {
    setMessages((prevMessages) => [
      ...prevMessages,
      { text: message, speaker: "EXTERNAL", dateTime },
    ]);
  };

  const createAgentMsg = (message: string, dateTime: string) => {
    setMessages((prevMessages) => [
      ...prevMessages,
      { text: message, speaker: "INTERNAL", dateTime },
    ]);
  };

  return (
    <div>
      <Transcript messages={messages} />
      <div ref={transcriptEndRef} />
    </div>
  );
};

export default App;
